import { memo, useMemo, useState } from 'react';
import { ShopTypes, UmbracoTypes } from '~/lib/data-contract';
import { useTranslations } from '~/services/umbraco/hooks';
import { DatePickerRange } from '~/shared/components/DatePicker';
import SearchField from '~/shared/components/SearchField';
import { Text } from '~/shared/components/Text';
import {
    setOrderHistoryTableQuery,
    setOrderHistoryTableStatus,
} from '~/state/useOrderHistoryState';
import { DropdownSelector } from '~/templates/blocks/shared/DropdownSelector';
import { ModuleContainer } from '~/templates/blocks/shared/ModuleContainer';
import { CurrentFilters } from '../../shared/CurrentFilters/CurrentFilters';
import TableSkeleton from './components/Skeleton';
import { Table } from './components/Table';
import useFilterOrderTableData from './components/useOrderTableData';
import { Filter, FilterWrapper, Header, SearchContainer, Section } from './styles';
import { useMyOrders } from '~/services/shop/hooks/mit-fog';

const SelectorMemo = memo(DropdownSelector);

export default function M200OrderHistory({ headline }: UmbracoTypes.IM200OrderHistoryModule) {
    const [dateRange, setDateRange] = useState<(Date | null)[]>([null, null]);
    const { translate } = useTranslations();
    const { data, isLoading } = useMyOrders();

    const options = useMemo(
        () => [
            {
                label: translate('mit-fog.orders.invoiced'),
                value: String(ShopTypes.OrderStatus.Invoiced),
            },
            {
                label: translate('mit-fog.orders.delivered'),
                value: String(ShopTypes.OrderStatus.Delivered),
            },
            {
                label: translate('mit-fog.orders.delivering'),
                value: String(ShopTypes.OrderStatus.Delivering),
            },
            {
                label: translate('mit-fog.orders.processing'),
                value: String(ShopTypes.OrderStatus.Processing),
            },
            {
                label: translate('mit-fog.orders.reserved'),
                value: String(ShopTypes.OrderStatus.Reserved),
            },
        ],

        [translate],
    );
    const { filteredData, query, selectedStatus } = useFilterOrderTableData(data ?? [], dateRange);

    if (isLoading || !data) return <TableSkeleton />;

    return (
        <ModuleContainer spacingTop="none">
            <Section>
                <Text as="h2" variant="display2">
                    {headline} ({filteredData?.length})
                </Text>
                <Header>
                    <SearchContainer>
                        <SearchField
                            type="text"
                            value={query}
                            setInputValue={setOrderHistoryTableQuery}
                            placeholder={translate('mit-fog.orderHistory.searchText')}
                        />
                    </SearchContainer>

                    <Filter>
                        <SelectorMemo
                            id="status"
                            label="Status"
                            options={options}
                            selectedItems={selectedStatus}
                            setSelectedItems={setOrderHistoryTableStatus}
                        />
                    </Filter>

                    <DatePickerRange
                        onChange={(dates) => (dates ? setDateRange(dates) : null)}
                        selectsRange
                        setDateRange={setDateRange}
                        startDate={dateRange[0]}
                        endDate={dateRange[1]}
                    />
                </Header>
                {selectedStatus.length > 0 && (
                    <FilterWrapper>
                        <CurrentFilters
                            filterElementList={selectedStatus}
                            setSelectedStatus={setOrderHistoryTableStatus}
                        />
                    </FilterWrapper>
                )}

                <Table data={filteredData} />
            </Section>
        </ModuleContainer>
    );
}
